/**
 * Input Validation and Sanitization Utilities
 * Prevents XSS, SQL Injection, Path Traversal, and other security vulnerabilities
 */

// Regex patterns for validation
const PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE: /^[\d\s\-\+\(\)]{7,15}$/,
  URL: /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/i,
  ALPHANUMERIC: /^[a-zA-Z0-9\s\-_.@]*$/,
  NUMERIC: /^\d+$/,
  SAFE_FILENAME: /^[a-zA-Z0-9._\-\s]+$/,
};

/**
 * Sanitizes string input to prevent XSS attacks
 * @param {string} input - The input to sanitize
 * @returns {string} - Sanitized input
 */
export const sanitizeString = (input) => {
  if (typeof input !== 'string') return '';
  
  return input
    .trim()
    // Remove HTML tags
    .replace(/<[^>]*>/g, '')
    // Remove JavaScript protocols
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    // Remove potential SQL injection patterns
    .replace(/['";\\]/g, (match) => {
      const escapeMap = { "'": "&#39;", '"': "&quot;", ";": "&#59;", "\\": "&#92;" };
      return escapeMap[match] || match;
    })
    // Remove control characters
    .replace(/[\x00-\x1F\x7F]/g, '');
};

/**
 * Sanitizes filename to prevent path traversal attacks
 * @param {string} filename - The filename to sanitize
 * @returns {string} - Sanitized filename
 */
export const sanitizeFilename = (filename) => {
  if (typeof filename !== 'string') return '';
  
  // Remove path traversal attempts
  let clean = filename
    .replace(/\.\.\//g, '')
    .replace(/\.\.\\/g, '')
    .replace(/^[\/\\]+/, '')
    .replace(/[<>:"|?*]/g, '');
  
  // Keep only safe characters
  clean = clean.replace(/[^\w\s.\-]/g, '');
  
  return clean.trim() || 'file';
};

/**
 * Validates email format
 * @param {string} email - Email to validate
 * @returns {object} - { isValid: boolean, error: string }
 */
export const validateEmail = (email) => {
  const sanitized = sanitizeString(email);
  
  if (!sanitized) {
    return { isValid: false, error: 'Email is required' };
  }
  
  if (sanitized.length > 254) {
    return { isValid: false, error: 'Email is too long' };
  }
  
  if (!PATTERNS.EMAIL.test(sanitized)) {
    return { isValid: false, error: 'Invalid email format' };
  }
  
  return { isValid: true, value: sanitized };
};

/**
 * Validates phone number format
 * @param {string} phone - Phone number to validate
 * @returns {object} - { isValid: boolean, error: string }
 */
export const validatePhone = (phone) => {
  const sanitized = sanitizeString(phone);
  
  if (!sanitized) {
    return { isValid: false, error: 'Phone number is required' };
  }
  
  if (!PATTERNS.PHONE.test(sanitized)) {
    return { isValid: false, error: 'Invalid phone number format' };
  }
  
  return { isValid: true, value: sanitized };
};

/**
 * Validates URL format
 * @param {string} url - URL to validate
 * @returns {object} - { isValid: boolean, error: string }
 */
export const validateUrl = (url) => {
  if (!url) {
    return { isValid: false, error: 'URL is required' };
  }
  
  const sanitized = sanitizeString(url);
  
  if (sanitized.length > 2048) {
    return { isValid: false, error: 'URL is too long' };
  }
  
  // Prevent javascript: protocol and data: protocol
  if (/^(javascript|data|vbscript):/i.test(sanitized)) {
    return { isValid: false, error: 'Invalid URL protocol' };
  }
  
  if (!PATTERNS.URL.test(sanitized)) {
    return { isValid: false, error: 'Invalid URL format' };
  }
  
  return { isValid: true, value: sanitized };
};

/**
 * Detects HTML tags, script tags, and other dangerous content
 * @param {string} text - Text to check
 * @returns {boolean} - True if dangerous content is detected
 */
const containsDangerousContent = (text) => {
  if (typeof text !== 'string') return false;
  
  // Check for HTML tags
  if (/<[^>]*>/i.test(text)) return true;
  
  // Check for script tags specifically
  if (/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi.test(text)) return true;
  
  // Check for event handlers (onclick, onerror, etc.)
  if (/on\w+\s*=/i.test(text)) return true;
  
  // Check for javascript protocol
  if (/javascript:/i.test(text)) return true;
  
  // Check for data protocol
  if (/data:text\/html/i.test(text)) return true;
  
  // Check for iframe, embed, object tags
  if (/<(iframe|embed|object)\b/i.test(text)) return true;
  
  return false;
};

/**
 * Validates text input (prevents XSS and injection)
 * @param {string} text - Text to validate
 * @param {object} options - { minLength, maxLength, allowSpecialChars, pattern, rejectHTML }
 * @returns {object} - { isValid: boolean, error: string, value: string }
 */
export const validateText = (text, options = {}) => {
  const {
    minLength = 1,
    maxLength = 500,
    allowSpecialChars = false,
    pattern = null,
    rejectHTML = false,
  } = options;

  // Check for dangerous content first (before sanitization)
  if (rejectHTML && containsDangerousContent(text)) {
    return { isValid: false, error: 'HTML tags and scripts are not allowed' };
  }

  const sanitized = sanitizeString(text);

  if (!sanitized) {
    return { isValid: false, error: `Text is required (min ${minLength} characters)` };
  }

  if (sanitized.length < minLength) {
    return { isValid: false, error: `Text must be at least ${minLength} characters` };
  }

  if (sanitized.length > maxLength) {
    return { isValid: false, error: `Text cannot exceed ${maxLength} characters` };
  }

  if (!allowSpecialChars && !/^[a-zA-Z0-9\s\-._,@'()]*$/.test(sanitized)) {
    return { isValid: false, error: 'Text contains invalid characters' };
  }

  if (pattern && !pattern.test(sanitized)) {
    return { isValid: false, error: 'Text format is invalid' };
  }

  return { isValid: true, value: sanitized };
};

/**
 * Validates numeric input
 * @param {string|number} value - Value to validate
 * @param {object} options - { min, max, allowDecimals }
 * @returns {object} - { isValid: boolean, error: string, value: number }
 */
export const validateNumeric = (value, options = {}) => {
  const { min = 0, max = null, allowDecimals = false } = options;

  const numValue = typeof value === 'string' ? value.trim() : value;

  if (numValue === '' || numValue === null || numValue === undefined) {
    return { isValid: false, error: 'Number is required' };
  }

  const parsed = allowDecimals ? parseFloat(numValue) : parseInt(numValue, 10);

  if (isNaN(parsed)) {
    return { isValid: false, error: 'Value must be a valid number' };
  }

  if (parsed < min) {
    return { isValid: false, error: `Value must be at least ${min}` };
  }

  if (max !== null && parsed > max) {
    return { isValid: false, error: `Value cannot exceed ${max}` };
  }

  return { isValid: true, value: parsed };
};

/**
 * Validates file upload
 * @param {File} file - File to validate
 * @param {object} options - { allowedTypes, maxSizeInMB, maxSizeInBytes }
 * @returns {object} - { isValid: boolean, error: string }
 */
export const validateFile = (file, options = {}) => {
  const {
    allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'],
    maxSizeInMB = 10,
  } = options;

  if (!file) {
    return { isValid: false, error: 'File is required' };
  }

  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

  // Validate filename
  const sanitizedName = sanitizeFilename(file.name);
  if (!sanitizedName || sanitizedName === 'file') {
    return { isValid: false, error: 'Invalid filename' };
  }

  // Validate file size
  if (file.size > maxSizeInBytes) {
    return { isValid: false, error: `File size cannot exceed ${maxSizeInMB}MB` };
  }

  // Validate file type
  if (!allowedTypes.includes(file.type)) {
    return { isValid: false, error: `File type not allowed. Allowed types: ${allowedTypes.join(', ')}` };
  }

  return { isValid: true, value: file };
};

/**
 * Validates video file
 * @param {File} file - Video file to validate
 * @param {object} options - { maxSizeInMB }
 * @returns {object} - { isValid: boolean, error: string }
 */
export const validateVideoFile = (file, options = {}) => {
  const { maxSizeInMB = 500 } = options;
  
  const videoTypes = [
    'video/mp4',
    'video/webm',
    'video/quicktime',
    'video/x-msvideo',
    'video/x-matroska',
    'video/mpeg',
  ];

  return validateFile(file, {
    allowedTypes: videoTypes,
    maxSizeInMB,
  });
};

/**
 * Validates image file
 * @param {File} file - Image file to validate
 * @param {object} options - { maxSizeInMB }
 * @returns {object} - { isValid: boolean, error: string }
 */
export const validateImageFile = (file, options = {}) => {
  const { maxSizeInMB = 5 } = options;

  const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

  return validateFile(file, {
    allowedTypes: imageTypes,
    maxSizeInMB,
  });
};

/**
 * Validates document file
 * @param {File} file - Document file to validate
 * @param {object} options - { maxSizeInMB }
 * @returns {object} - { isValid: boolean, error: string }
 */
export const validateDocumentFile = (file, options = {}) => {
  const { maxSizeInMB = 20 } = options;

  const docTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
  ];

  return validateFile(file, {
    allowedTypes: docTypes,
    maxSizeInMB,
  });
};

/**
 * Validates a date string
 * @param {string} dateStr - Date string to validate
 * @param {object} options - { minDate, maxDate, format }
 * @returns {object} - { isValid: boolean, error: string, value: string }
 */
export const validateDate = (dateStr, options = {}) => {
  if (!dateStr) {
    return { isValid: false, error: 'Date is required' };
  }

  const date = new Date(dateStr);
  
  if (isNaN(date.getTime())) {
    return { isValid: false, error: 'Invalid date format' };
  }

  return { isValid: true, value: dateStr };
};

/**
 * Validates route parameter (prevents injection)
 * @param {string} param - Route parameter to validate
 * @param {object} options - { type, pattern }
 * @returns {object} - { isValid: boolean, error: string, value: string }
 */
export const validateRouteParam = (param, options = {}) => {
  const { type = 'alphanumeric', pattern = null } = options;

  if (!param) {
    return { isValid: false, error: 'Parameter is required' };
  }

  const stringParam = String(param).trim();

  if (type === 'numeric') {
    if (!PATTERNS.NUMERIC.test(stringParam)) {
      return { isValid: false, error: 'Parameter must be numeric' };
    }
  } else if (type === 'alphanumeric') {
    if (!/^[a-zA-Z0-9\-_]+$/.test(stringParam)) {
      return { isValid: false, error: 'Parameter contains invalid characters' };
    }
  }

  if (pattern && !pattern.test(stringParam)) {
    return { isValid: false, error: 'Parameter format is invalid' };
  }

  return { isValid: true, value: stringParam };
};

/**
 * Batch validation of form data
 * @param {object} data - Form data to validate
 * @param {object} rules - Validation rules for each field
 * @returns {object} - { isValid: boolean, errors: object, values: object }
 */
export const validateFormData = (data, rules) => {
  const errors = {};
  const values = {};
  let isValid = true;

  for (const [field, rule] of Object.entries(rules)) {
    const value = data[field];
    let result = { isValid: false, error: 'Unknown error' };

    if (rule.type === 'email') {
      result = validateEmail(value);
    } else if (rule.type === 'phone') {
      result = validatePhone(value);
    } else if (rule.type === 'url') {
      result = validateUrl(value);
    } else if (rule.type === 'text') {
      result = validateText(value, rule.options);
    } else if (rule.type === 'numeric') {
      result = validateNumeric(value, rule.options);
    } else if (rule.type === 'date') {
      result = validateDate(value, rule.options);
    } else if (rule.type === 'file') {
      result = validateFile(value, rule.options);
    }

    if (!result.isValid) {
      errors[field] = result.error;
      isValid = false;
    } else {
      values[field] = result.value || value;
    }
  }

  return { isValid, errors, values };
};

/**
 * Prevents common OWASP Top 10 vulnerabilities
 * @param {object} input - Input object to sanitize
 * @returns {object} - Sanitized input object
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'object' || input === null) {
    return input;
  }

  const sanitized = {};
  
  for (const [key, value] of Object.entries(input)) {
    // Sanitize key to prevent prototype pollution
    if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
      continue;
    }

    if (typeof value === 'string') {
      sanitized[key] = sanitizeString(value);
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeInput(value);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
};

/**
 * Validates Indian phone number (10 digits, cannot start with 0)
 * @param {string} phone - Phone number to validate
 * @returns {object} - { isValid: boolean, error: string, value: string }
 */
export const validatePhoneIndia = (phone) => {
  const sanitized = sanitizeString(phone);
  
  if (!sanitized) {
    return { isValid: false, error: 'Phone number is required' };
  }

  // Remove all non-digit characters for validation
  const digitsOnly = sanitized.replace(/\D/g, '');
  
  // Check if it's exactly 10 digits
  if (digitsOnly.length !== 10) {
    return { isValid: false, error: 'Phone number must be exactly 10 digits' };
  }
  
  // Check if it starts with 0
  if (digitsOnly[0] === '0') {
    return { isValid: false, error: 'Phone number cannot start with 0' };
  }
  
  // Check if all are digits
  if (!/^\d{10}$/.test(digitsOnly)) {
    return { isValid: false, error: 'Phone number must contain only digits' };
  }
  
  return { isValid: true, value: digitsOnly };
};

/**
 * Validates latitude and longitude coordinates
 * @param {string} coords - Coordinates string (format: "latitude,longitude" or "latitude longitude")
 * @returns {object} - { isValid: boolean, error: string, value: string }
 */
export const validateCoordinates = (coords) => {
  const sanitized = sanitizeString(coords);
  
  if (!sanitized) {
    return { isValid: false, error: 'Coordinates are required' };
  }

  // Support both comma and space separated formats
  const parts = sanitized.split(/[,\s]+/).filter(p => p.trim());
  
  if (parts.length !== 2) {
    return { isValid: false, error: 'Coordinates must be in format: latitude, longitude' };
  }

  const lat = parseFloat(parts[0]);
  const lon = parseFloat(parts[1]);

  // Validate that both are numbers
  if (isNaN(lat) || isNaN(lon)) {
    return { isValid: false, error: 'Coordinates must be valid numbers' };
  }

  // Validate latitude range (-90 to 90)
  if (lat < -90 || lat > 90) {
    return { isValid: false, error: 'Latitude must be between -90 and 90' };
  }

  // Validate longitude range (-180 to 180)
  if (lon < -180 || lon > 180) {
    return { isValid: false, error: 'Longitude must be between -180 and 180' };
  }

  // Check decimal places (typically GPS has up to 8 decimal places for precision)
  const latDecimalPlaces = parts[0].split('.')[1]?.length || 0;
  const lonDecimalPlaces = parts[1].split('.')[1]?.length || 0;
  
  if (latDecimalPlaces > 8 || lonDecimalPlaces > 8) {
    return { isValid: false, error: 'Coordinates have too many decimal places' };
  }

  return { isValid: true, value: `${lat},${lon}` };
};

export default {
  sanitizeString,
  sanitizeFilename,
  validateEmail,
  validatePhone,
  validatePhoneIndia,
  validateUrl,
  validateText,
  validateNumeric,
  validateFile,
  validateVideoFile,
  validateImageFile,
  validateDocumentFile,
  validateDate,
  validateCoordinates,
  validateRouteParam,
  validateFormData,
  sanitizeInput,
};